import { Directive, ElementRef, HostListener, InjectFlags, Injector, forwardRef } from '@angular/core';
import { AbstractControl, NG_VALIDATORS, NgControl, ValidationErrors, Validator, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core'; 
import { ToastService } from 'src/app/services/toast.service';
import { AppConstants } from 'src/app/utilities/constant';

@Directive({
  // app-ai-writing-field-shell is included because it replaced plain
  // textarea/ion-textarea on several required fields; it is a
  // ControlValueAccessor, so it validates the same way they did.
  selector: 'input[ngModel], input[formControlName], input[formControl], textarea[ngModel], textarea[formControlName], textarea[formControl], ion-input[ngModel], ion-input[formControlName], ion-input[formControl], ion-textarea[ngModel], ion-textarea[formControlName], ion-textarea[formControl], app-ai-writing-field-shell[ngModel], app-ai-writing-field-shell[formControlName], app-ai-writing-field-shell[formControl]',
  providers: [
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => NoWhitespaceOnlyValidatorDirective),
      multi: true
    }
  ]
})
export class NoWhitespaceOnlyValidatorDirective implements Validator {
  constructor(
    private readonly injector: Injector,
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly toastService: ToastService,
    private readonly translate: TranslateService
  ) {}

  validate(control: AbstractControl): ValidationErrors | null {
    const value = control.value;

    if (!this.isMandatoryControl(control) || typeof value !== 'string' || value.length === 0) {
      return null;
    }

    return value.trim().length === 0 ? { whitespaceOnly: true } : null;
  }

  // Pass the raw event object into the host listeners
  @HostListener('blur', ['$event'])
  @HostListener('focusout', ['$event'])
  @HostListener('ionBlur', ['$event'])
  clearWhitespaceAndShowToast(event: Event): void {
    const control = this.injector.get(NgControl, null, InjectFlags.Self | InjectFlags.Optional)?.control;
    const value = control?.value;

    if (!control || !this.isMandatoryControl(control) || typeof value !== 'string' || value.length === 0 || value.trim().length > 0) {
      return;
    }

    // 1. CRITICAL: Stop component-level auto-save functions from executing
    if (event) {
      event.preventDefault();
      event.stopImmediatePropagation(); // Kills other blur/focusout events on this element
    }

    // 2. Clear values and force the specific validation error code
    control.setValue('', { emitEvent: true });
    control.markAsTouched();
    control.setErrors({ whitespaceOnly: true });

    // 3. Display your centralized notification overlay
    this.toastService.presentToast(
      this.translate.instant('only_spaces_are_not_allowed'),
      4000,
      AppConstants.ERROR_SNACKBAR,
      'top'
    );
  }

  private isMandatoryControl(control: AbstractControl): boolean {
    return control.hasValidator(Validators.required) || this.hasRequiredAttribute();
  }

  private hasRequiredAttribute(): boolean {
    const nativeElement = this.elementRef.nativeElement;
    const requiredAttribute = nativeElement.getAttribute('required');

    if (requiredAttribute !== null && requiredAttribute.toLowerCase() !== 'false') {
      return true;
    }

    const ariaRequired = nativeElement.getAttribute('aria-required');
    if (ariaRequired === 'true') {
      return true;
    }

    const dataRequired = nativeElement.getAttribute('data-required');
    if (dataRequired !== null && dataRequired.toLowerCase() !== 'false') {
      return true;
    }

    const whitespaceRequired = nativeElement.getAttribute('data-whitespace-required');
    return whitespaceRequired !== null && whitespaceRequired.toLowerCase() !== 'false';
  }
}
